<!-- Active call surface.
     Layouts (picked automatically):
       solo  — alone, waiting: my tile + a placeholder
       duo   — one other person: they fill the window, I am a small picture-in-picture
       grid  — several people: equal tiles
       stage — someone shares their screen (or a tile is pinned): it fills the
               window, everyone else in a strip underneath
     Click a tile to pin it on stage, double-click the stage to go back. -->
<template>
  <div class="call-window" :class="'layout-' + layout">
    <div v-if="stageTile" class="call-stage" @dblclick="pinned = null">
      <call-tile :tile="stageTile" stage :fit="stageTile.sharing ? 'contain' : 'cover'" :sink-id="audioOut" />
      <button v-if="pinned !== null && !stageTile.sharing" class="call-stage__unpin" title="Back to the grid" @click="pinned = null">
        <feather-icon icon="GridIcon" svgClasses="w-4 h-4" />
      </button>
    </div>

    <div class="call-tiles" :class="tilesClass">
      <call-tile v-for="t in otherTiles" :key="t.id" :tile="t" :sink-id="audioOut"
                 :clickable="layout !== 'duo' && participants.length >= 2"
                 @select="togglePin(t)" />
      <div v-if="participants.length === 0" class="call-placeholder">
        <template v-if="pending.length">
          <div class="call-placeholder__avatars">
            <span v-for="p in pending" :key="p.userId" class="call-placeholder__ring call-placeholder__ring--avatar">
              <vs-avatar :src="p.avatar" :text="p.username || '?'" size="56px" />
            </span>
          </div>
          <span class="call-placeholder__title">Calling {{ pendingNames }}…</span>
          <span class="call-placeholder__hint">Ringing on their side</span>
        </template>
        <template v-else>
          <span class="call-placeholder__ring"><feather-icon icon="UsersIcon" svgClasses="w-7 h-7" /></span>
          <span class="call-placeholder__title">Waiting for someone to join</span>
          <span class="call-placeholder__hint">Use “Add people” to invite a colleague</span>
        </template>
      </div>
    </div>

    <div class="call-window__controls">
      <vs-button radius :color="mic ? 'dark' : 'danger'" type="filled" icon-pack="feather"
                 :icon="mic ? 'icon-mic' : 'icon-mic-off'" :title="mic ? 'Mute' : 'Unmute'" @click="m.toggleMic()" />
      <vs-button radius :color="cam ? 'dark' : 'danger'" type="filled" icon-pack="feather"
                 :icon="cam ? 'icon-video' : 'icon-video-off'" :disabled="!hasCamera"
                 :title="hasCamera ? (cam ? 'Turn camera off' : 'Turn camera on') : 'No camera found — audio only'"
                 @click="m.toggleCam()" />
      <vs-button radius :color="sharing ? 'primary' : 'dark'" type="filled" icon-pack="feather"
                 icon="icon-monitor" :title="sharing ? 'Stop sharing' : 'Share your screen'" @click="toggleShare" />
      <vs-button radius color="dark" type="filled" icon-pack="feather"
                 icon="icon-user-plus" title="Add people to the call" @click="m.openCallPanel()" />
      <vs-button radius color="dark" type="filled" icon-pack="feather"
                 icon="icon-settings" title="Microphone, camera & speakers" @click="m.openDeviceCheck()" />
      <vs-button radius color="danger" type="filled" icon-pack="feather"
                 icon="icon-phone-off" title="Leave the call" @click="m.requestHangup()" />
    </div>
  </div>
</template>

<script>
import meeting from '@/services/meeting.js'
import CallTile from './CallTile.vue'

export default {
  components: { CallTile },
  data () { return { m: meeting, pinned: null } },
  computed: {
    participants () { return meeting.state.participants },
    mic () { return meeting.state.mic },
    cam () { return meeting.state.cam },
    hasCamera () { return meeting.state.hasCamera },
    sharing () { return meeting.state.sharing },
    pending () { return meeting.state.pendingInvites },
    pendingNames () { return this.pending.map(p => p.username).filter(Boolean).join(', ') },
    audioOut () { return meeting.state.devices.audioOut || '' },
    tiles () {
      const tick = meeting.state.streamTick // re-evaluate when a stream changes
      const s = meeting.state
      const me = {
        id: 'me',
        local: true,
        name: s.sharing ? 'You · screen' : 'You',
        avatar: null,
        tick,
        stream: meeting.getLocalPreviewStream(),
        mic: s.mic,
        cam: s.cam,
        sharing: s.sharing,
        connected: true,
        link: 'ok',
        away: false
      }
      const others = s.participants.map(p => ({
        id: p.userId,
        local: false,
        name: p.username || '…',
        avatar: p.avatar,
        tick,
        stream: meeting.getParticipantStream(p.userId),
        mic: p.mic,
        cam: p.cam,
        sharing: p.sharing,
        connected: p.connected,
        link: p.link,
        away: p.away
      }))
      return [me, ...others]
    },
    stageTile () {
      const sharer = this.tiles.find(t => !t.local && t.sharing)
      if (sharer) return sharer
      if (this.pinned !== null) {
        const p = this.tiles.find(t => t.id === this.pinned)
        if (p) return p
      }
      if (this.participants.length === 1) return this.tiles[1]
      return null
    },
    otherTiles () {
      return this.stageTile ? this.tiles.filter(t => t !== this.stageTile) : this.tiles
    },
    layout () {
      if (!this.stageTile) return this.participants.length ? 'grid' : 'solo'
      return (this.otherTiles.length === 1 && this.otherTiles[0].local) ? 'duo' : 'stage'
    },
    tilesClass () {
      if (this.layout === 'stage') return 'call-tiles--strip'
      if (this.layout === 'duo') return 'call-tiles--pip'
      const n = this.otherTiles.length + (this.participants.length === 0 ? 1 : 0)
      if (n <= 2) return 'call-tiles--2'
      if (n <= 4) return 'call-tiles--4'
      return 'call-tiles--many'
    }
  },
  watch: {
    participants (list) {
      if (this.pinned !== null && this.pinned !== 'me' && !list.some(p => p.userId === this.pinned)) this.pinned = null
    }
  },
  methods: {
    togglePin (t) { this.pinned = this.pinned === t.id ? null : t.id },
    toggleShare () {
      if (this.sharing) meeting.stopShareScreen()
      else meeting.shareScreen()
    }
  }
}
</script>

<style scoped>
.call-window {
  position: relative; width: 100%; height: 100%; min-height: 100%;
  background: #0f0f1e; border-radius: 12px; overflow: hidden;
  display: flex; flex-direction: column;
}

.call-stage { position: relative; flex: 1; min-height: 0; padding: 10px 10px 0; display: flex; }
.call-stage >>> .call-tile { flex: 1; }
.call-stage__unpin {
  position: absolute; top: 18px; right: 18px; z-index: 6;
  width: 30px; height: 30px; border: none; border-radius: 8px; cursor: pointer;
  background: rgba(0,0,0,.55); color: #fff; display: inline-flex; align-items: center; justify-content: center;
}
.call-stage__unpin:hover { background: rgba(0,0,0,.8); }

.call-tiles { display: grid; gap: 8px; padding: 10px; padding-bottom: 74px; min-height: 0; }
.layout-solo .call-tiles, .layout-grid .call-tiles { flex: 1; }
.call-tiles--2 { grid-template-columns: 1fr 1fr; grid-auto-rows: 1fr; }
.call-tiles--4 { grid-template-columns: 1fr 1fr; grid-auto-rows: 1fr; }
.call-tiles--many { grid-template-columns: repeat(3, 1fr); grid-auto-rows: 1fr; }

/* stage: the others in a strip under the stage */
.call-tiles--strip {
  flex: 0 0 auto; display: flex; gap: 8px; overflow-x: auto; padding-bottom: 74px;
}
.call-tiles--strip >>> .call-tile { flex: 0 0 150px; height: 96px; }

/* duo: I am a small picture-in-picture over the other person */
.layout-duo .call-stage { padding: 10px; }
.call-tiles--pip { position: absolute; right: 18px; bottom: 82px; z-index: 5; padding: 0; width: 26%; min-width: 130px; max-width: 220px; }
.call-tiles--pip >>> .call-tile { aspect-ratio: 4 / 3; box-shadow: 0 6px 20px rgba(0,0,0,.5); border: 1px solid rgba(255,255,255,.12); }

.call-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
  color: rgba(255,255,255,.6); font-size: 13px; text-align: center; padding: 10px;
  background: #15152a; border-radius: 12px; border: 1px dashed rgba(255,255,255,.12);
}
.call-placeholder__title { color: #fff; font-size: 14px; font-weight: 600; }
.call-placeholder__hint { font-size: 12px; color: rgba(255,255,255,.5); }
.call-placeholder__avatars { display: flex; gap: 12px; }
.call-placeholder__ring {
  width: 56px; height: 56px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  background: rgba(115,103,240,.2); color: #a59cff; animation: call-ring 1.6s infinite;
}
.call-placeholder__ring--avatar { background: transparent; }
.call-placeholder__ring--avatar >>> .con-vs-avatar { margin: 0; }
@keyframes call-ring {
  0% { box-shadow: 0 0 0 0 rgba(115,103,240,.5); }
  70% { box-shadow: 0 0 0 16px rgba(115,103,240,0); }
  100% { box-shadow: 0 0 0 0 rgba(115,103,240,0); }
}

.call-window__controls {
  position: absolute; left: 0; right: 0; bottom: 16px; z-index: 7;
  display: flex; gap: 14px; justify-content: center;
}
.call-window__controls >>> .vs-button { box-shadow: 0 4px 14px rgba(0,0,0,.45); }
</style>
